import { PART_TYPES, FACE_RENDER_TYPES } from '../faceParts/data/enums'
import facialLinesSubTypes from '../faceParts/data/facialLines/facialLinesSubTypes'
import jewelrySubTypes from '../faceParts/data/jewelry/jewelrySubTypes'
import generalConfig from '../config'
import faceData from '../faceParts/data/data'
import initialValues from '../state/initialValues'
import { MALE_HAIR_IDS } from '../faceParts/data/constants'

// for any future partTypes with subTypes, import their subTypes above & add them to the subSets object below
const subSets = {
  facialLines: facialLinesSubTypes,
  jewelry: jewelrySubTypes,
}

export const deepCopyObj = (obj) => JSON.parse(JSON.stringify(obj))

export const getCroppedSVGContentsViewport = ({
  svg = { children: [] },
  partType,
  override = { x: 0, y: 0, height: 0, width: 0 },
}) => {
  if (svg.children.length === 0) return null
  const focusOffsets = {
    [PART_TYPES.JEWELRY_EARRINGS]: { x: -30, y: 0 },
    [PART_TYPES.MOUTH]: { x: 0, y: 10 },
    [PART_TYPES.NOSE]: { x: 0, y: -10 },
    [PART_TYPES.EYES]: { x: 0, y: -20 },
    [PART_TYPES.JEWELRY_NECKLACES]: { x: 0, y: 40 },
    [PART_TYPES.EYEBROWS]: { x: 0, y: -20 },
    [PART_TYPES.GLASSES]: { x: 0, y: -10 },
    [PART_TYPES.FACIAL_LINES_FOREHEAD]: { x: 0, y: -30 },
    [PART_TYPES.FACIAL_LINES_EYES]: { x: 0, y: -12 },
  }
  const part = focusOffsets[partType] ?? { x: 0, y: -4 }

  // See https://typeofnan.dev/how-to-perfectly-fit-an-svg-to-its-contents-using-javascript/
  const { xMin, xMax, yMin, yMax } = [...svg.children].reduce((acc, el) => {
    const { x, y, width, height } = el.getBBox()
    if (!acc.xMin || x < acc.xMin) acc.xMin = x
    if (!acc.xMax || x + width > acc.xMax) acc.xMax = x + width
    if (!acc.yMin || y < acc.yMin) acc.yMin = y
    if (!acc.yMax || y + height > acc.yMax) acc.yMax = y + height
    return acc
  }, {})
  const viewbox = `${xMin + part.x} ${yMin + part.y + override.y} ${xMax - xMin} ${yMax - yMin + override.height}`

  return viewbox
}

export const getInitialPosition = ({ partType, partId, sectionsArr, faceType }) => {
  const section = sectionsArr.find((sec) => {
    return sec.partType === partType
  })

  const individualPart = section?.partVarieties?.find((part) => part.id === partId)

  const position =
    partType === PART_TYPES.FACE_SHAPE ? individualPart?.positioning : individualPart?.positioning?.[faceType]

  return position
}

/**
 * Get the first valid ID for a part type (used as fallback for invalid IDs)
 */
const getFirstValidId = (partType) => {
  const partData = faceData[partType]
  if (!partData || partData.length === 0) return ''

  // Skip 'Blank' if there are other options and the part has default selection
  const nonBlankOptions = partData.filter((item) => item.id !== 'Blank')
  if (nonBlankOptions.length > 0) {
    return nonBlankOptions[0].id
  }

  return partData[0].id
}

export const getSVG = ({ partType, id }) => {
  if (!partType || !id) return null

  return (
    faceData[partType].find((item) => {
      return item.id === id || (id === 'none' && item.id === 'Blank')
    })?.svg ?? null
  )
}

export const getScale = ({ partType, id }) => {
  if (!partType || !id) return null
  return (
    faceData[partType].find((item) => {
      return item.id === id || (id === 'none' && item.id === 'Blank')
    })?.scaleX ?? null
  )
}

const getSortedShirts = ({ maleShirts, femaleShirts, gender }) => {
  if (gender === 'MALE') return [...maleShirts, ...femaleShirts]
  return [...femaleShirts, ...maleShirts]
}

const getSortedHair = ({ allHair, gender }) => {
  const [maleHair, femaleHair] = allHair.reduce(
    (acc, hairData) => {
      if (hairData.id === 'Blank') return acc

      const strippedId = parseInt(hairData.id.split('Hair')[1], 10)
      if (MALE_HAIR_IDS.includes(strippedId)) acc[0].push(hairData)
      else acc[1].push(hairData)

      return acc
    },
    [[], []]
  )

  const blank = allHair[0]
  if (gender === 'MALE') return [blank, ...maleHair, ...femaleHair]
  return [blank, ...femaleHair, ...maleHair]
}

const getFacePartData = (allPartsSections, partType) => {
  const match = allPartsSections.find((partSection) => {
    return partSection.partType === partType
  })

  return match.partVarieties
}

/** exported for testing */
export const downloadPicture = ({ image, name }) => {
  const link = document.createElement('a')
  link.download = name
  link.href = image
  link.click()
}

export const populate = (gender) => {
  const { maleShirts, femaleShirts, hair: allHair, ...genderNeutralData } = faceData
  const shirt = getSortedShirts({ maleShirts, femaleShirts, gender })
  const hair = getSortedHair({ allHair, gender })

  const allPartsSections = Object.entries({ shirt, hair, ...genderNeutralData }).map(([partType, partVarieties]) => {
    return { partType, partVarieties }
  })

  const partsSections = [...generalConfig].map((section) => {
    // for any future partTypes that have subSets, add their partType to the array
    const partTypeSubsets = [PART_TYPES.FACIAL_LINES, PART_TYPES.JEWELRY]
    if (partTypeSubsets.includes(section.partType)) {
      const subTypeSet = subSets[section.partType]
      section.partVarieties = subTypeSet.map((subSet) => {
        return {
          partType: subSet.subType,
          data: getFacePartData(allPartsSections, subSet.subType),
          title: subSet.title,
        }
      })
      return section
    }

    section.partVarieties = [{ partType: section.partType, data: getFacePartData(allPartsSections, section.partType) }]
    return section
  })

  return { partsSections }
}

/**
 * This is what we get from the server.
 * Mobile only sends parts that are selected, everything else is the default. This puts those defaults back into state.
 */
export const getAvatarParts = (savedParts) => {
  const parts = { ...savedParts }
  Object.entries(initialValues.avatarPartsConfig).forEach(([key, value]) => {
    // if saved part has no value, assign it the initial/default Value
    // FYI: must deep-copy this value. Not doing so was causing initialValues to be overwritten which initialized new avatars incorrectly.
    // Added some Object.freeze sanity checks in initialValues to catch this error
    if (!parts[key]) parts[key] = deepCopyObj(value)
    else {
      const { primaryColor, ...rest } = parts[key]
      // Merge with initial values to preserve properties like availableActions
      parts[key] = { ...deepCopyObj(value), colorKey: primaryColor, ...rest }

      // Validate that the ID is valid for parts that should have a default selection
      // If the ID is invalid (doesn't exist in face data), use the first valid option
      if (parts[key].hasDefaultSelection && parts[key].id) {
        const partType = key
        const svg = getSVG({ id: parts[key].id, partType })
        if (!svg) {
          // Invalid ID - use the first valid option for this part type
          parts[key].id = getFirstValidId(partType)
        }
      }
    }
  })
  return parts
}

/**
 * Mobile references primaryColor as the colorKey
 * @returns the object that will get sent to the backend
 */
export const getAvatarSpecs = (avatarPartsConfig) => {
  const specs = Object.fromEntries(
    Object.entries(avatarPartsConfig).map(([key, value]) => {
      const { colorKey, ...rest } = value
      return [key, { primaryColor: colorKey, ...rest }]
    })
  )
  return specs
}

/**
 * @returns Whether the hair has an accompanying back svg
 */
const hasHairBack = (id) => {
  const { hairBack } = faceData
  const hairBackResult = hairBack.find(({ id: backId }) => backId.split('back')[0] === id)
  return Boolean(hairBackResult)
}

/**
 * updated the avatar parts to include the accompying hair back svg
 */
export const updateHairBack = ({ avatarPartsConfig, partType, id }) => {
  const avatarParts = window.structuredClone(avatarPartsConfig)

  if (partType !== PART_TYPES.HAIR) return avatarParts

  avatarParts.hairBack = {
    id: hasHairBack(id) ? `${id}back` : '',
  }

  return avatarParts
}

/**
 * Get the width and height that the SVG should use
 */
export const getSvgHeight = ({ mode, atWidth }) => {
  if (mode === FACE_RENDER_TYPES.CIRCLE_FRAMED) {
    return [atWidth({ default: 261, md: 400 }), atWidth({ default: 261, md: 400 })]
  }

  if (mode === FACE_RENDER_TYPES.SQUARE_FRAMED) {
    return [atWidth({ default: 150, md: 200 }), atWidth({ default: 200, md: 266 })]
  }

  return [320, 320]
}
